import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Card, CardContent, Button, TextField } from '@mui/material';
import { format, subMonths, startOfMonth, toDate } from 'date-fns';
import { getCenterOfHopeSummary } from '../../scripts/api/webAppAnalyticsApi.js';
import CenterOfHopeSummaryDto from '../../DTOs/WebAppAnalytics/CenterOfHopeSummaryDto.js';
import DateRangeDto from '../../DTOs/shared/DateRangeDto.js';
import { message } from '../shared/Message.js';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import DataTable from './shared/DataTable';
import OtherItemsList from './shared/OtherItemsList';

function CenterOfHopeSummary() {
  const [centerOfHopeSummaryDto, setCenterOfHopeSummaryDto] = useState(new CenterOfHopeSummaryDto());
  const [startDate, setStartDate] = useState(format(startOfMonth(subMonths(new Date(), 3)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [fetchStartDate, setFetchStartDate] = useState(startDate);
  const [fetchEndDate, setFetchEndDate] = useState(endDate);
  const fetchData = async (start, end) => {
    const dateRangeDto = new DateRangeDto({
      startDate: toDate(new Date(start)).toISOString(),
      endDate: toDate(new Date(end)).toISOString(),
    });
    try {
      let result = await getCenterOfHopeSummary(JSON.stringify(dateRangeDto));
      setCenterOfHopeSummaryDto(result);
    } catch (err) {
      message.error(err.message);
    }
  };

  // Map bar chart categories into the desired format
  const transformDataForBarChart = (data) => {
    if (!data || !data.categories || !data.series) {
      return [];
    }
    return data.categories.map((category, index) => {
      const transformedCategory = { category }; // Add the category name
      data.series.forEach((series) => {
        transformedCategory[series.name.toLowerCase().replace(/\s+/g, '_')] = series.data[index] || 0; // Add series data
      });
      return transformedCategory;
    });
  };
  const barrierStackedBarChartData = transformDataForBarChart(centerOfHopeSummaryDto.barrierStackedBarChartData);
  const goalStackedBarChartData = transformDataForBarChart(centerOfHopeSummaryDto.goalStackedBarChartData);

  const pieColors = [
    '#1976d2', // Blue
    '#9c27b0', // Purple
    '#ff9800', // Orange
    '#43a047', // Green
    '#e53935', // Red
    '#00bcd4', // Cyan
    '#fbc02d', // Yellow
    '#8d6e63', // Brown
    '#7e57c2', // Deep Purple
    '#388e3c', // Dark Green
    '#f06292', // Pink
    '#6d4c41', // Dark Brown
    '#0288d1', // Light Blue
    '#c2185b', // Magenta
    '#afb42b', // Olive
    '#00897b', // Teal
    '#d84315', // Rust
    '#5d4037', // Coffee
    '#cddc39', // Lime
    '#ffb300', // Amber
    '#3949ab', // Indigo
    '#039be5', // Sky Blue
    '#d4e157', // Light Lime
    '#8e24aa', // Violet
    '#00acc1', // Turquoise
    '#e65100', // Burnt Orange
  ];

  useEffect(() => {
    fetchData(fetchStartDate, fetchEndDate);
  }, [fetchStartDate, fetchEndDate]);

  const handleFetchData = () => {
    setFetchStartDate(startDate);
    setFetchEndDate(endDate);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {/* Date Range and Fetch Button */}
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFetchData}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
         </Box>

        {/* Barriers Section */}
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            {/* Row 1: PieChart (12/12) */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Referenced Parent Barriers
                  </Typography>
                  {centerOfHopeSummaryDto.barrierCircleChartData && (
                    <PieChart
                      colors={pieColors}
                      series={[
                        {
                          data: centerOfHopeSummaryDto.barrierCircleChartData.labels.map((label, index) => ({
                            id: label,
                            value: centerOfHopeSummaryDto.barrierCircleChartData.values[index],
                            label: `${label} (${Math.round(
                              (centerOfHopeSummaryDto.barrierCircleChartData.values[index] /
                                centerOfHopeSummaryDto.barrierCircleChartData.total) *
                              100
                            )}%)`,
                          })),
                        },
                      ]}
                      height={400}
                      slotProps={{
                        legend: {
                          direction: 'horizontal',
                          position: { vertical: 'bottom', horizontal: 'center' }
                        }
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Row 2: BarChart (12/12) */}
            <Grid size={12}>
              <Card>
                <CardContent sx={{ width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Most Referenced Parent Barriers by Progress
                  </Typography>
                  <Box sx={{ width: '100%' }}>
                    <BarChart
                      yAxis={[{
                        data: barrierStackedBarChartData.map((item) => item.category),
                        scaleType: 'band',
                        width: 150
                      }]}
                      xAxis={[{ tickMinStep: 1 }]}
                      series={[
                        { label: "New", data: barrierStackedBarChartData.map((item) => item.new), stack: 'bar' },
                        { label: "Making Progress", data: barrierStackedBarChartData.map((item) => item.making_progress), stack: 'bar' },
                        { label: "Removed", data: barrierStackedBarChartData.map((item) => item.removed), stack: 'bar' },
                      ]}
                      height={400}
                      layout="horizontal"
                      slotProps={{
                        legend: {
                          direction: 'horizontal',
                          position: { vertical: 'bottom', horizontal: 'center' }
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Row 3: Two tables side by side (6/12 each) */}
            <Grid size={12}>
              <Grid container spacing={3}>
                <Grid size={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ width: '100%', minWidth: 0 }}>
                      <Typography variant="h6" gutterBottom>
                        Total References
                      </Typography>
                      <DataTable
                        data={{
                          headers: ["Barrier", "Count"],
                          rows: centerOfHopeSummaryDto.barrierCount.map(b => [b.key, b.value])
                        }}
                        title="Total References"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        References by Progress
                      </Typography>
                      <DataTable
                        data={{
                          headers: ["Barrier", "Progress", "Count"],
                          rows: centerOfHopeSummaryDto.barrierProgressCount.map(b => [
                            b.barrier,
                            b.progress,
                            b.count
                          ])
                        }}
                        title="References by Progress"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 4: Other Parent Barriers (12/12) */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Other Parent Barriers
                  </Typography>
                  <OtherItemsList items={centerOfHopeSummaryDto.barrierText.map(b => b.barrier)} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Goals Section */}
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            {/* Circle Chart for Goals */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Referenced Parent Goals
                  </Typography>
                  <Box sx={{  }}>
                    {centerOfHopeSummaryDto.goalCircleChartData && (
                      <PieChart
                        colors={pieColors}
                        series={[
                          {
                            data: centerOfHopeSummaryDto.goalCircleChartData.labels.map((label, index) => ({
                              id: label,
                              value: centerOfHopeSummaryDto.goalCircleChartData.values[index],
                              label: `${label} (${Math.round(
                                (centerOfHopeSummaryDto.goalCircleChartData.values[index] /
                                  centerOfHopeSummaryDto.goalCircleChartData.total) *
                                100
                              )}%)`,
                            })),
                          },
                        ]}
                        height={400}
                        slotProps={{
                          legend: {
                            direction: 'horizontal',
                            position: {
                              vertical: 'bottom',
                              horizontal: 'center'
                            }
                          }
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Bar Chart for Goal Progress */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Most Referenced Parent Goals by Progress
                  </Typography>
                  <Box sx={{  }}>
                    <BarChart
                      yAxis={[{
                        data: goalStackedBarChartData.map((item) => item.category),
                        scaleType: 'band',
                        width: 150
                      }]} // Use categories directly
                      xAxis={[{
                        tickMinStep: 1
                      }]}
                      series={[
                        { label: "New", data: goalStackedBarChartData.map((item) => item.new), stack: 'bar' },
                        { label: "Making Progress", data: goalStackedBarChartData.map((item) => item.making_progress), stack: 'bar' },
                        { label: "Met", data: goalStackedBarChartData.map((item) => item.met), stack: 'bar' },
                      ]}
                      height={400}
                      layout="horizontal"
                      slotProps={{
                        legend: {
                          direction: 'horizontal',
                          position: {
                            vertical: 'bottom',
                            horizontal: 'center'
                          }
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Tables for Goals */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Parent Goals Details
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Total References Table */}
                    <Grid size={6}>
                      {centerOfHopeSummaryDto.goalCount && centerOfHopeSummaryDto.goalCount.length > 0 ? (
                        <>
                          <DataTable
                            data={{
                              headers: ["Goal", "Count"],
                              rows: centerOfHopeSummaryDto.goalCount.map(b => [b.key, b.value])
                            }}
                            title="Total References"
                          />
                        </>
                      ) : (
                        <Typography variant="body2">No data available for goals.</Typography>
                      )}
                    </Grid>

                    {/* References by Progress Table */}
                    <Grid size={6}>
                      <DataTable
                        data={{
                          headers: ["Goal", "Progress", "Count"],
                          rows: centerOfHopeSummaryDto.goalProgressCount.map(g => [
                            g.goal,
                            g.progress,
                            g.count
                          ])
                        }}
                        title="References by Progress"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Other Goals Section */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Other Parent Goals
                  </Typography>
                  <OtherItemsList items={centerOfHopeSummaryDto.goalText.map(g => g.goal)} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
}

export default CenterOfHopeSummary;
